import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2, AlertTriangle, X, Save, BookOpen, Ban } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type DailyMenu = {
  id: string;
  name: string;
  type: string;
  validFrom: string;
  validTo: string;
  recurrencePattern: string | null;
  startTimeOfDay: string | null;
  endTimeOfDay: string | null;
  isActive: boolean;
  itemCount: number;
};

type EightySixed = {
  id: string;
  productId: string;
  productName: string;
  reason: string | null;
  eightySixedAt: string;
};

type Location = { id: string; name: string };

export function LocationDailyMenusPage() {
  const { id: locationId } = useParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [menus, setMenus] = useState<DailyMenu[]>([]);
  const [eightySixed, setEightySixed] = useState<EightySixed[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creatingMenu, setCreatingMenu] = useState(false);
  const [deleteMenuTarget, setDeleteMenuTarget] = useState<DailyMenu | null>(null);
  const [clearing86Target, setClearing86Target] = useState<EightySixed | null>(null);

  const refresh = useCallback(async () => {
    if (!locationId) return;
    try {
      const [loc, m, e] = await Promise.all([
        api.get<Location>(`/admin/locations/${locationId}`),
        api.get<DailyMenu[]>(`/admin/locations/${locationId}/daily-menus`),
        api.get<EightySixed[]>(`/admin/locations/${locationId}/eighty-sixed`),
      ]);
      setLocation(loc);
      setMenus(m);
      setEightySixed(e);
    } catch (er: any) {
      setError(er?.data?.message || er?.message || 'Greška');
    }
  }, [locationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div>
      <Link to="/locations" style={backLink}><ChevronLeft size={14} /> Lokacije</Link>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            <BookOpen size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Meni: {location?.name || '...'}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Dnevni meniji, happy hour, 86'd liste
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreatingMenu(true)} style={btnPrimary}>
          <Plus size={14} /> Novi meni
        </button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

      <h3 style={sectionTitle}>Dnevni meniji ({menus.length})</h3>
      <div style={card}>
        {menus.length === 0 ? (
          <div style={emptyState}>
            <BookOpen size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
            <div>Nema definisanih menija</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Naziv</th><th style={th}>Tip</th><th style={th}>Period</th><th style={th}>Vreme</th>
                <th style={{ ...th, textAlign: 'right' }}>Stavki</th><th style={th}>Status</th>
                <th style={{ ...th, width: 80, textAlign: 'right' }}>Akcije</th>
              </tr></thead>
              <tbody>
                {menus.map((m) => (
                  <tr key={m.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{m.name}</td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{m.type}</td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{formatDate(m.validFrom)} → {formatDate(m.validTo)}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {m.startTimeOfDay && m.endTimeOfDay ? `${m.startTimeOfDay}–${m.endTimeOfDay}` : 'Ceo dan'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{m.itemCount}</td>
                    <td style={td}>{m.isActive ? <span style={badgeActive}>Aktivan</span> : <span style={badgeInactive}>Off</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => setDeleteMenuTarget(m)} style={{ ...iconBtn, color: '#fca5a5' }}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h3 style={{ ...sectionTitle, marginTop: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Ban size={14} /> 86'd lista ({eightySixed.length})
      </h3>
      <div style={card}>
        {eightySixed.length === 0 ? (
          <div style={emptyState}>Nema proizvoda na 86 listi</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Proizvod</th><th style={th}>Razlog</th><th style={th}>Otkad</th>
                <th style={{ ...th, width: 80, textAlign: 'right' }}>Akcije</th>
              </tr></thead>
              <tbody>
                {eightySixed.map((e) => (
                  <tr key={e.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{e.productName}</td>
                    <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{e.reason || '-'}</td>
                    <td style={{ ...td, fontSize: 11, color: 'var(--color-text-muted)' }}>{new Date(e.eightySixedAt).toLocaleString('sr-RS')}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => setClearing86Target(e)} style={iconBtn} title="Vrati u prodaju">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creatingMenu && <DailyMenuFormDialog locationId={locationId!} onClose={() => setCreatingMenu(false)} onSaved={() => { setCreatingMenu(false); refresh(); }} />}

      {deleteMenuTarget && (
        <ConfirmDialog
          title="Obriši meni"
          message={`Da li želite da obrišete meni "${deleteMenuTarget.name}"?`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={async () => {
            await api.del(`/admin/locations/${locationId}/daily-menus/${deleteMenuTarget.id}`);
            refresh();
          }}
          onClose={() => setDeleteMenuTarget(null)}
        />
      )}

      {clearing86Target && (
        <ConfirmDialog
          title="Vrati proizvod u prodaju"
          message={`Da li želite da uklonite "${clearing86Target.productName}" sa 86 liste?`}
          confirmLabel="Vrati u prodaju"
          variant="default"
          onConfirm={async () => {
            await api.del(`/admin/locations/${locationId}/eighty-sixed/${clearing86Target.id}`);
            refresh();
          }}
          onClose={() => setClearing86Target(null)}
        />
      )}
    </div>
  );
}

function DailyMenuFormDialog({ locationId, onClose, onSaved }: { locationId: string; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [name, setName] = useState('');
  const [type, setType] = useState('daily');
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [startTimeOfDay, setStartTimeOfDay] = useState('');
  const [endTimeOfDay, setEndTimeOfDay] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Naziv obavezan'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/locations/${locationId}/daily-menus`, {
        name: name.trim(),
        type,
        validFrom: new Date(validFrom).toISOString(),
        validTo: new Date(validTo + 'T23:59:59').toISOString(),
        startTimeOfDay: startTimeOfDay || undefined,
        endTimeOfDay: endTimeOfDay || undefined,
      });
      onSaved();
    } catch (e: any) { setError(e?.data?.message || e?.message || 'Greška'); }
    finally { setSaving(false); }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Novi dnevni meni</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Naziv *">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Happy Hour, Lunch Special..." autoFocus required style={input} />
          </Field>
          <Field label="Tip">
            <select value={type} onChange={(e) => setType(e.target.value)} style={input}>
              <option value="daily">Dnevni meni</option>
              <option value="happy_hour">Happy Hour</option>
              <option value="brunch">Brunch</option>
              <option value="special">Specijalni</option>
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Važi od *">
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required style={input} />
            </Field>
            <Field label="Važi do *">
              <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} required style={input} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Početak (HH:mm, opciono)">
              <input type="time" value={startTimeOfDay} onChange={(e) => setStartTimeOfDay(e.target.value)} style={input} />
            </Field>
            <Field label="Kraj (HH:mm, opciono)">
              <input type="time" value={endTimeOfDay} onChange={(e) => setEndTimeOfDay(e.target.value)} style={input} />
            </Field>
          </div>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}><Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>{children}</div>;
}

const backLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, textDecoration: 'none' };
const sectionTitle: React.CSSProperties = { margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const emptyState: React.CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' };
const th: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border-light)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const badgeActive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', fontWeight: 700 };
const badgeInactive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
